// hooks/useR0006Data.js - Enhanced version building on your existing structure
import { useState, useEffect, useCallback, useMemo } from 'react';
import { differenceInDays, parseISO } from 'date-fns';
import { API } from 'aws-amplify';
import { dbGetItem } from 'graphql/queries';
import useAreas from 'hooks/clients/grm/useAreas';
import { useUser } from 'components/RDAuthContext';
import { fetchSalesPerformanceData } from '../services/salesPerformanceService';
import { fetchLaborHoursData, calculateLaborHoursTotals } from '../services/laborHoursService';
import { R0006_CONFIG } from '../constants';
import useR0006Access from './useR0006Access';

export default function useR0006Data() {
  // External hooks for area/store data - use filteredData to exclude closed stores
  const { filteredData: areasData, storeIdMap: idMap, storeMap, loading: areasLoading, error: areasError } = useAreas();
  const { user } = useUser();
  
  // Get all stores from areas data (which contains the GM information)
  const allStoresFromAreas = useMemo(() => {
    if (!areasData) return [];
    const stores = [];
    areasData.forEach(area => {
      if (area.stores && Array.isArray(area.stores)) {
        area.stores.forEach(store => {
          stores.push(store);
        });
      }
    });
    return stores;
  }, [areasData]);
  
  // Debug: Log store structure
  useEffect(() => {
    // Store structure validation (removed debug logs)
  }, [allStoresFromAreas]);
  
  // Access control hook
  const {
    hasAccess,
    hasFullAccess,
    accessLevel,
    accessMessage,
    filteredAreas,
    filteredStores,
    userArea,
    canAccessArea,
    canAccessStore,
    filterReportData
  } = useR0006Access(areasData, allStoresFromAreas);

  // Log user details for debugging (after access control hook)
  // User access validation (removed debug logs)


  // Create storeIdMap for the sales performance service using the correct store mapping
  const createCorrectStoreIdMap = useCallback(() => {
    const map = new Map();
    
      if (storeMap && storeMap.size > 0) {
      for (const [longName, storeData] of storeMap.entries()) {
        // Use storeRecordId which is what MetricData uses as the storeId in our environment
        const storeRecordId = storeData.storeRecordId;
        if (storeRecordId) {
          // Map both long name and short name to the same ID
          // R0003 data uses shortName, but storeMap is keyed by longName
          map.set(longName, storeRecordId);
          if (storeData.shortName && storeData.shortName !== longName) {
            map.set(storeData.shortName, storeRecordId);
          }
        }
      }
    }
    return map;
  }, [storeMap]);

  // Use access-controlled areas instead of all areas
  const CURRENT_AREA_TYPE = 'Company'; // TODO: Make this configurable via props or state
  // All access-controlled areas, any areaType. The sales service still needs these
  // for per-store theo hours / threshold lookups (e.g. the "All Stores" view).
  const accessibleAreasData = useMemo(() => filteredAreas || [], [filteredAreas]);
  // Areas shown in the roll-up and selector. The R0003 resolver applies no areaType
  // filter for Admin/Exec, so Franchise areas with entered projections would
  // otherwise get a roll-up row — see docs/r0006-franchise-area-investigation.md.
  const filteredAreasData = useMemo(
    () => accessibleAreasData.filter(area => area.areaType === CURRENT_AREA_TYPE),
    [accessibleAreasData]
  );
  // Labor hours bucket by R0003's area name, so they need their own guard
  const allowedAreaNames = useMemo(
    () => new Set(filteredAreasData.map(area => area.areaName)),
    [filteredAreasData]
  );

  // Removed excessive logging

  // Your existing state structure
  const [dateRange, setDateRange] = useState(null);
  const [selectedArea, setSelectedArea] = useState(R0006_CONFIG.DEFAULT_AREA);
  const [reportData, setReportData] = useState(null);
  const [originalReportData, setOriginalReportData] = useState(null); // Store original unfiltered data
  const [dataReady, setDataReady] = useState(false);

  // Your existing loading states
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [pendingSelection, setPendingSelection] = useState(null);
  const [dataError, setDataError] = useState(null);

  // State for area management
  const [selectedAreaData, setSelectedAreaData] = useState(null);

  // State for threshold data (avgCheck adjustments)
  const [thresholds, setThresholds] = useState([]);

  // Fetch threshold configuration on mount (same pattern as R0003)
  useEffect(() => {
    async function getThresholds() {
      if (!user?.client) return;

      const dbVar = {
        client: user.client,
        report: 'R0002',
        itemType: 'thresholds',
        type: []
      };
      const dbVarJSON = JSON.stringify(dbVar);

      try {
        const queryResponse = await API.graphql({
          query: dbGetItem,
          variables: {
            db: 'grm.reportadmins',
            dbVariables: dbVarJSON
          }
        });
        const responseData = JSON.parse(JSON.parse(queryResponse.data.dbGetItem.statusItem));
        setThresholds(responseData);
      } catch (error) {
        // Error fetching thresholds
        setThresholds([]); // Set empty array on error
      }
    }

    getThresholds();
  }, [user]);

  // For area-restricted users, don't auto-select - let them choose from their areas
  // They will see "Area" level view by default showing all their accessible areas
  // This provides consistent behavior with full-access users who see "Area Summary" view

  // Helper function from your existing code
  const dateToYMDString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to convert store data to area-aggregated view
  const convertToAreaAggregatedView = useCallback((storeData) => {
    // Determine if this is for a restricted user (show "Area" instead of "Area Summary")
    const isRestrictedView = !hasFullAccess;
    // If no areas data is available, return the original data with a flag
    if (!filteredAreasData?.length) {
      return {
        ...storeData,
        viewType: 'stores' // Show as stores view when no areas available
      };
    }

    if (!storeData?.restaurants) {
      return storeData;
    }

    const areaAggregatedData = [];
    let brandTotalSales = 0;
    let brandTotalOpportunity = 0;

    // Aggregate data for each area
    filteredAreasData.forEach(area => {
      const areaStoreNames = area.stores.map(store => store.shortName);

      // Since storeData has already been filtered by filterReportData to include
      // all stores in accessible areas, we don't need additional filtering here
      const areaRestaurants = storeData.restaurants.filter(restaurant =>
        areaStoreNames.includes(restaurant.store)
      );

      if (areaRestaurants.length > 0) {
        // Calculate totals for aggregation
        const totalProjSales = areaRestaurants.reduce((sum, r) => sum + (r.projSales || 0), 0);
        const totalProjCheckCount = areaRestaurants.reduce((sum, r) => sum + (r.projCheckCount || 0), 0);
        const totalActCheckCount = areaRestaurants.reduce((sum, r) => sum + (r.actCheckCount || 0), 0);

        // Calculate base aggregated projAvgCheck (total projection / total check count)
        const baseAggregatedProjAvgCheck = totalProjCheckCount > 0
          ? totalProjSales / totalProjCheckCount
          : 0;

        // Apply simple average adjustment for area rollup (matches R0003.2 SPLH pattern)
        // Formula: (sum of all store adjustments) / count of stores with assignments
        // IMPORTANT: Only include stores that have an explicit threshold assignment
        // A store with assignment value of 0 should be included; a store with no assignment should be excluded
        const storeAdjustments = [];
        areaRestaurants.forEach(r => {
          // Check if store has an explicit threshold assignment (not just non-zero adjustment)
          if (r._hasThresholdAssignment) {
            const adjustment = r._avgCheckAdjustment || 0;
            storeAdjustments.push(adjustment);
          }
        });

        const areaAverageAdjustment = storeAdjustments.length > 0
          ? storeAdjustments.reduce((sum, adj) => sum + adj, 0) / storeAdjustments.length / 100 // Convert cents to dollars
          : 0;

        const adjustedAggregatedProjAvgCheck = baseAggregatedProjAvgCheck + areaAverageAdjustment;

        // Sum up all metrics for this area
        const areaAggregated = {
          store: area.areaName, // Use area name as the "store" name for display
          areaId: area.areaId,
          // Sales metrics
          projSales: totalProjSales,
          actSales: areaRestaurants.reduce((sum, r) => sum + (r.actSales || 0), 0),
          projCheckCount: totalProjCheckCount,
          actCheckCount: totalActCheckCount,
          projAvgCheck: adjustedAggregatedProjAvgCheck, // ADJUSTED aggregated projected avg check
          opportunity: areaRestaurants.reduce((sum, r) => sum + (r.opportunity || 0), 0),
          // Labor metrics
          schedFOH: areaRestaurants.reduce((sum, r) => sum + (r.schedFOH || 0), 0),
          modelFOH: areaRestaurants.reduce((sum, r) => sum + (r.modelFOH || 0), 0),
          actFOH: areaRestaurants.reduce((sum, r) => sum + (r.actFOH || 0), 0),
          adjFOH: areaRestaurants.reduce((sum, r) => sum + (r.adjFOH || 0), 0),
          schedBOH: areaRestaurants.reduce((sum, r) => sum + (r.schedBOH || 0), 0),
          modelBOH: areaRestaurants.reduce((sum, r) => sum + (r.modelBOH || 0), 0),
          actBOH: areaRestaurants.reduce((sum, r) => sum + (r.actBOH || 0), 0),
          adjBOH: areaRestaurants.reduce((sum, r) => sum + (r.adjBOH || 0), 0),
          // Store count for reference
          storeCount: areaRestaurants.length,
          // Debug fields
          _unadjustedProjAvgCheck: baseAggregatedProjAvgCheck,
          _avgCheckAdjustment: areaAverageAdjustment * 100, // Store in cents for consistency
          _hasThresholdAssignment: storeAdjustments.length > 0, // Area has assignment if any stores do
          // Store the list of store adjustments for totals calculation
          _storeAdjustments: storeAdjustments
        };

        areaAggregatedData.push(areaAggregated);
        brandTotalSales += areaAggregated.actSales;
        brandTotalOpportunity += areaAggregated.opportunity;
      }
    });

    // Sort areas alphabetically by area name
    areaAggregatedData.sort((a, b) => a.store.localeCompare(b.store));

    // Aggregate labor hours data if it exists
    let aggregatedLaborHours = null;
    let aggregatedLaborHoursTotals = null;

    if (storeData.laborHours && storeData.laborHours.length > 0) {
      // Group labor hours by area
      const laborHoursByArea = {};

      storeData.laborHours.forEach(storeLabor => {
        // The new labor service provides areaName directly, use it
        const areaName = storeLabor.areaName;

        if (areaName && allowedAreaNames.has(areaName)) {
          if (!laborHoursByArea[areaName]) {
            laborHoursByArea[areaName] = {
              area: areaName,
              foh: {
                schedVsModel: 0,
                changeDuringDay: 0,
                varianceToAdjusted: 0,
                allowedSPLH: 0,
                // Add raw hour values for tooltips
                schedHours: 0,
                theoHours: 0,
                actualHours: 0,
                adjHours: 0,
                trainingHours: 0
              },
              boh: {
                schedVsModel: 0,
                changeDuringDay: 0,
                varianceToAdjusted: 0,
                allowedSPLH: 0,
                // Add raw hour values for tooltips
                schedHours: 0,
                theoHours: 0,
                actualHours: 0,
                adjHours: 0,
                trainingHours: 0
              }
            };
          }

          // Sum up the calculated values
          laborHoursByArea[areaName].foh.schedVsModel += storeLabor.foh.schedVsModel;
          laborHoursByArea[areaName].foh.changeDuringDay += storeLabor.foh.changeDuringDay;
          laborHoursByArea[areaName].foh.varianceToAdjusted += storeLabor.foh.varianceToAdjusted;
          laborHoursByArea[areaName].foh.allowedSPLH += storeLabor.foh.allowedSPLH;
          laborHoursByArea[areaName].boh.schedVsModel += storeLabor.boh.schedVsModel;
          laborHoursByArea[areaName].boh.changeDuringDay += storeLabor.boh.changeDuringDay;
          laborHoursByArea[areaName].boh.varianceToAdjusted += storeLabor.boh.varianceToAdjusted;
          laborHoursByArea[areaName].boh.allowedSPLH += storeLabor.boh.allowedSPLH;

          // Sum up the raw hour values for tooltips
          laborHoursByArea[areaName].foh.schedHours += (Number(storeLabor.foh.schedHours) || 0);
          laborHoursByArea[areaName].foh.theoHours += (Number(storeLabor.foh.theoHours) || 0);
          laborHoursByArea[areaName].foh.actualHours += (Number(storeLabor.foh.actualHours) || 0);
          laborHoursByArea[areaName].foh.adjHours += (Number(storeLabor.foh.adjHours) || 0);
          laborHoursByArea[areaName].foh.trainingHours += (Number(storeLabor.foh.trainingHours) || 0);
          laborHoursByArea[areaName].boh.schedHours += (Number(storeLabor.boh.schedHours) || 0);
          laborHoursByArea[areaName].boh.theoHours += (Number(storeLabor.boh.theoHours) || 0);
          laborHoursByArea[areaName].boh.actualHours += (Number(storeLabor.boh.actualHours) || 0);
          laborHoursByArea[areaName].boh.adjHours += (Number(storeLabor.boh.adjHours) || 0);
          laborHoursByArea[areaName].boh.trainingHours += (Number(storeLabor.boh.trainingHours) || 0);
        }
      });

      aggregatedLaborHours = Object.values(laborHoursByArea);
      // Sort labor hours alphabetically by area name to match sales table
      aggregatedLaborHours.sort((a, b) => a.area.localeCompare(b.area));
      aggregatedLaborHoursTotals = calculateLaborHoursTotals(aggregatedLaborHours);
    }

    // Create the aggregated report data structure
    return {
      ...storeData,
      restaurants: areaAggregatedData,
      totalSales: brandTotalSales,
      opportunity: brandTotalOpportunity,
      laborHours: aggregatedLaborHours || storeData.laborHours,
      laborHoursTotals: aggregatedLaborHoursTotals || storeData.laborHoursTotals,
      viewType: 'areas', // Flag to indicate this is area-aggregated view
      isRestrictedView, // Flag to indicate if this is for a restricted user
      totalRowLabel: isRestrictedView ? 'AREA TOTALS' : 'TOTALS' // Custom label for totals row
    };
  }, [filteredAreasData, allowedAreaNames, hasFullAccess]);

  // Helper function to convert store data to all-stores view (alphabetically sorted)
  const convertToAllStoresView = useCallback((storeData) => {
    if (!storeData?.restaurants) {
      return storeData;
    }

    // Filter out "Area Rollup" rows and stores outside the Company areas (e.g. Franchise),
    // then sort all restaurants alphabetically by store name
    const filteredAndSortedRestaurants = storeData.restaurants
      .filter(restaurant => !restaurant.store.startsWith('Area Rollup'))
      .filter(restaurant => allowedAreaNames.has(restaurant.areaName))
      .sort((a, b) => a.store.localeCompare(b.store));

    // Same filtering for labor hours; here `area` holds the store name and
    // `areaName` the store's area
    let sortedLaborHours = storeData.laborHours;
    let laborHoursTotals = storeData.laborHoursTotals;
    if (storeData.laborHours && storeData.laborHours.length > 0) {
      sortedLaborHours = storeData.laborHours
        .filter(laborData => !laborData.area.startsWith('Area Rollup'))
        .filter(laborData => allowedAreaNames.has(laborData.areaName))
        .sort((a, b) => a.area.localeCompare(b.area));
      // Recalculate so excluded stores don't leak into the totals row
      laborHoursTotals = sortedLaborHours.length > 0 ? calculateLaborHoursTotals(sortedLaborHours) : null;
    }

    // Recalculate totals without Area Rollup rows
    const totalSales = filteredAndSortedRestaurants.reduce((sum, restaurant) => sum + (restaurant.actSales || 0), 0);
    const totalOpportunity = filteredAndSortedRestaurants.reduce((sum, restaurant) => sum + (restaurant.opportunity || 0), 0);

    return {
      ...storeData,
      restaurants: filteredAndSortedRestaurants,
      laborHours: sortedLaborHours,
      laborHoursTotals,
      totalSales,
      opportunity: totalOpportunity,
      viewType: 'all-stores', // Flag to indicate this is all-stores view
      totalRowLabel: 'TOTALS'
    };
  }, [allowedAreaNames]);


  // Real data loading function using the sales performance service
  const loadRealData = async (dateRangeAsStrings, forceSelectedArea = null) => {
    if (!user?.userName || !user?.role) {
      throw new Error('User authentication required');
    }

    const correctStoreIdMap = createCorrectStoreIdMap();

    let salesData = null;
    let salesError = null;
    let laborHoursData = null;
    let laborError = null;

    // Fetch sales data first (this includes R0003 data)
    try {
      salesData = await fetchSalesPerformanceData(
        dateRangeAsStrings,
        forceSelectedArea === R0006_CONFIG.DEFAULT_AREA ? null : forceSelectedArea,
        user,
        user.role,
        correctStoreIdMap,
        accessibleAreasData,
        thresholds
      );

    } catch (error) {
      salesError = error;
    }
      
    // Fetch labor hours data using the same R0003 data from sales
    try {
      
      // If sales succeeded, pass the R0003 data to avoid duplicate query
      const r0003Data = salesData?.__r0003Data || null;
      
      // For GenMgr users, we need to pass their accessible stores even when viewing top level
      // to ensure they get data for stores they manage
      let storesFilter = [];
      if (forceSelectedArea !== R0006_CONFIG.DEFAULT_AREA && forceSelectedArea !== 'Area') {
        // Specific area selected
        storesFilter = [forceSelectedArea];
      } else if (!hasFullAccess && filteredStores && filteredStores.length > 0) {
        // GenMgr or area-restricted user viewing top level - pass their accessible stores
        storesFilter = filteredStores.map(store => store.shortName || store.name);
      } else {
        // Full access user viewing top level - no filter needed
        storesFilter = [];
      }
      
      laborHoursData = await fetchLaborHoursData(
        dateRangeAsStrings.startDate,
        dateRangeAsStrings.endDate,
        user,
        user.role,
        storesFilter,
        correctStoreIdMap,
        r0003Data // Pass R0003 data to avoid duplicate query
      );
    } catch (error) {
      laborError = error;
    }
    
    // If both failed, throw an error
    if (!salesData && !laborHoursData) {
      const combinedError = new Error(`Both data sources failed. Sales: ${salesError?.message || 'Unknown'}. Labor: ${laborError?.message || 'Unknown'}`);
      throw combinedError;
    }
    
    // Create merged data with whatever we have
    const mergedData = {
      ...(salesData || {}),
      restaurants: salesData?.restaurants || [],
      laborHours: laborHoursData || [],
      laborHoursTotals: laborHoursData?.length ? calculateLaborHoursTotals(laborHoursData) : null,
      // Add flags to indicate what data is available
      hasSalesData: !!salesData,
      hasLaborData: !!laborHoursData,
      salesError: salesError?.message,
      laborError: laborError?.message
    };
    
    return mergedData;
  };

  // Your existing handleDateRangeSubmit with enhanced data generation and area reset
  const handleDateRangeSubmit = async (newDateRange) => {
    const dateRangeAsStrings = {
      startDate: dateToYMDString(newDateRange.from),
      endDate: dateToYMDString(newDateRange.to || newDateRange.from),
      isRange: newDateRange.from !== newDateRange.to
    };

    console.log('🔄 Starting data load for date range:', dateRangeAsStrings);

    // Reset area selection when loading new date range
    // Use "Area" for restricted users, "Area Summary" for full access users
    const topLevelName = hasFullAccess ? R0006_CONFIG.DEFAULT_AREA : 'Area';
    setSelectedArea(topLevelName);
    setSelectedAreaData(null);

    setPageLoading(true);
    setDataReady(false);
    setPendingSelection(dateRangeAsStrings);
    setLoadingProgress(0);
    setLoadingMessage('Initializing...');
    setDataError(null); // Clear any previous errors

    try {
      const startDate = parseISO(dateRangeAsStrings.startDate);
      const endDate = parseISO(dateRangeAsStrings.endDate);
      const daysDiff = differenceInDays(endDate, startDate) + 1;
      
  
      // Your existing progress simulation
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          const newProgress = prev + Math.random() * 12;
          
          if (newProgress < 25) {
            setLoadingMessage('Connecting to data source...');
          } else if (newProgress < 50) {
            setLoadingMessage('Processing date range...');
          } else if (newProgress < 75) {
            setLoadingMessage('Calculating metrics...');
          } else if (newProgress < 95) {
            setLoadingMessage('Finalizing report...');
          } else {
            setLoadingMessage('Almost ready...');
          }
          
          return Math.min(newProgress, 98);
        });
      }, 150);
      
      const loadingTime = dateRangeAsStrings.isRange ? 
        Math.min(daysDiff * 200, 3000) : 1500;
      
      await new Promise(resolve => setTimeout(resolve, loadingTime));
      clearInterval(progressInterval);
      
      setLoadingProgress(100);
      setLoadingMessage('Complete!');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Load real data only - no mock data fallback
      try {
        const reportDataResult = await loadRealData(dateRangeAsStrings, R0006_CONFIG.DEFAULT_AREA);

        // Store the original data (individual stores with area rollups)
        setOriginalReportData(reportDataResult);

        // Apply access control filter to the data (aggregated view initially)
        const accessFilteredData = filterReportData(reportDataResult, 'aggregated');

        // Convert to area-aggregated view for Area Summary display
        const areaAggregatedData = convertToAreaAggregatedView(accessFilteredData);

        setDateRange(dateRangeAsStrings);
        setReportData(areaAggregatedData);
        setDataReady(true);

      } catch (realDataError) {
        
        // DO NOT use mock data - set error state instead
        setDataError(realDataError);
        setLoadingMessage(`Failed to load data: ${realDataError.message || 'Unknown error'}`);
        setDataReady(false);
        setReportData(null);
        setOriginalReportData(null);
        return; // Exit early on error
      }
      
    } catch (error) {
      setDataError(error);
      setLoadingMessage(`Error: ${error.message || 'Unknown error'}`);
      setDataReady(false);
      setReportData(null);
      setOriginalReportData(null);
    } finally {
      setPageLoading(false);
      setPendingSelection(null);
      setLoadingProgress(0);
      // Keep the error message visible if there was an error
      if (!dataError) {
        setLoadingMessage('');
      }
    }
  };

  // Enhanced area change handler that shows aggregated areas or filters to specific area stores
  const handleAreaChange = (newArea) => {

    // For area-restricted users, only allow navigation to their assigned areas
    if (!hasFullAccess) {
      // Check if they're trying to navigate to an area they have access to or "Area" top level or "All Stores"
      const canAccessNewArea = filteredAreas.some(area =>
        area.areaName === newArea || area.name === newArea
      );

      const isTopLevel = newArea === 'Area' || newArea === 'Area Summary' || newArea === R0006_CONFIG.DEFAULT_AREA;
      const isAllStores = newArea === 'All Stores';

      if (!canAccessNewArea && !isTopLevel && !isAllStores) {
        return;
      }
    }

    if (!originalReportData) {
      return;
    }

    // Check for "All Stores" view - show all stores alphabetically
    if (newArea === 'All Stores') {
      setSelectedArea('All Stores');
      setSelectedAreaData(null);

      // Apply access control filter for aggregated view
      const accessFilteredData = filterReportData(originalReportData, 'aggregated');
      // Convert to all-stores view (alphabetically sorted)
      const allStoresData = convertToAllStoresView(accessFilteredData);
      setReportData(allStoresData);
      return;
    }

    // Check for "back to area summary/areas" signals - show aggregated area data
    // For restricted users, use "Area" as the top level instead of "Area Summary"
    const topLevelName = hasFullAccess ? R0006_CONFIG.DEFAULT_AREA : 'Area';

    if (newArea === 'all' || newArea === 'Area Summary' || newArea === 'Area' || !newArea || newArea === R0006_CONFIG.DEFAULT_AREA) {
      setSelectedArea(topLevelName);
      setSelectedAreaData(null);

      // Apply access control filter for aggregated view
      const accessFilteredData = filterReportData(originalReportData, 'aggregated');
      // Convert to area-aggregated view
      const aggregatedReportData = convertToAreaAggregatedView(accessFilteredData);
      setReportData(aggregatedReportData);
      return;
    }

    // Find the area data from filtered areas - compare by areaName since newArea is the area name
    const areaData = filteredAreasData?.find(area => area.areaName === newArea);
    if (!areaData) {
      return;
    }

    setSelectedArea(newArea);
    setSelectedAreaData(areaData);

    // Filter the original reportData to only show stores from the selected area
    if (originalReportData?.restaurants) {
      const areaStoreNames = areaData.stores.map(store => store.shortName);

      const filteredRestaurants = originalReportData.restaurants.filter(restaurant =>
        areaStoreNames.includes(restaurant.store)
      );

      // Sort stores alphabetically by store name
      filteredRestaurants.sort((a, b) => a.store.localeCompare(b.store));

      // Filter labor hours data if it exists
      let filteredLaborHours = originalReportData.laborHours;
      let filteredLaborHoursTotals = originalReportData.laborHoursTotals;

      if (originalReportData.laborHours && originalReportData.laborHours.length > 0) {
        filteredLaborHours = originalReportData.laborHours.filter(laborData =>
          areaStoreNames.includes(laborData.area)
        );
        // Sort labor hours alphabetically by store name to match sales table
        filteredLaborHours.sort((a, b) => a.area.localeCompare(b.area));
        filteredLaborHoursTotals = calculateLaborHoursTotals(filteredLaborHours);
      }

      // Update the report data with filtered results, maintaining the same structure
      const filteredReportData = {
        ...originalReportData,
        restaurants: filteredRestaurants,
        // Recalculate totals based on filtered data
        totalSales: filteredRestaurants.reduce((sum, restaurant) => sum + (restaurant.actSales || 0), 0),
        opportunity: filteredRestaurants.reduce((sum, restaurant) => sum + (restaurant.opportunity || 0), 0),
        laborHours: filteredLaborHours,
        laborHoursTotals: filteredLaborHoursTotals,
        viewType: 'stores' // Flag to indicate this is store-level view
      };

      // Apply access control filter for area-stores view (shows all stores in accessible areas)
      const accessFilteredData = filterReportData(filteredReportData, 'area-stores');
      setReportData(accessFilteredData);
    }
  };

  // Table row click handler - could be used for future functionality
  const handleTableRowClick = useCallback((rowData) => {
    // Future functionality can be added here
  }, []);

  // Your existing export handler
  const handleExport = () => {
  };

  // Computed properties
  const areaName = selectedAreaData?.areaName || '';

  return {
    // Your existing returns
    dateRange,
    selectedArea,
    reportData,
    dataReady,
    pageLoading,
    loadingProgress,
    loadingMessage,
    pendingSelection,
    dataError,
    handleDateRangeSubmit,
    handleAreaChange,
    handleExport,

    // Area functionality
    selectedAreaData,
    areasData: filteredAreasData,
    areasLoading,
    areasError,
    areaName,
    handleTableRowClick,

    // Access control information
    hasAccess,
    hasFullAccess,
    accessLevel,
    accessMessage,

    // Threshold data for AdjustmentFootnote
    thresholds,
    storeMap // Provides store threshold assignments
  };
}